'use client';

import {
  type HTMLAttributes,
  createContext,
  useContext,
  useEffect,
  useRef,
} from 'react';

export interface ActiveSlide {
  /** Index (registration order) of the currently-shown slide. */
  activeIndex: number;
  /** Whether the lightbox is open at all. */
  open: boolean;
}

/**
 * The lightbox's active slide, published by the attachment strip and read by
 * each {@link LightboxVideo}. Lives above `PhotoProvider`; React context flows
 * through react-photo-view's portal, so the portaled `render` nodes still see it.
 */
export const ActiveSlideContext = createContext<ActiveSlide>({
  activeIndex: 0,
  open: false,
});

interface Props {
  /** Sizing + slide-transform attrs handed in by `PhotoView`'s `render`. */
  attrs: Partial<HTMLAttributes<HTMLElement>>;
  src: string;
  /** This video's index in the gallery (its tile order). */
  index: number;
}

/**
 * A video slide inside the attachment lightbox. It loads and plays ONLY while it
 * is the active slide: `src` is set only when active (an off-screen/adjacent
 * slide never fetches), and playback is driven imperatively — play on becoming
 * active, pause when leaving. Opened/advanced via a user gesture, so `play()` is
 * allowed; if a browser still blocks it, the native controls start it.
 */
export function LightboxVideo({ attrs, src, index }: Props) {
  const { activeIndex, open } = useContext(ActiveSlideContext);
  const isActive = open && activeIndex === index;
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (isActive) {
      void el.play().catch(() => {
        // Autoplay blocked — the controls let the viewer start it.
      });
    } else {
      el.pause();
    }
  }, [isActive]);

  return (
    <div {...attrs}>
      <video
        ref={videoRef}
        src={isActive ? src : undefined}
        controls
        playsInline
        preload="none"
        className="h-full w-full object-contain"
      />
    </div>
  );
}
